import { FormSectionHandler } from '../entity.handler';
import { LabelResponse, LabelUpdateRequest, LabelValuePayload } from '@key-telematics/fleet-api-client';
import { AdminEditorService } from '../../admin.service';
import { FormBuilderField, FormBuilderKeyValue } from 'app/shared/components/form-builder';
import { TextField, EntityUtils, CheckListField, ComboField } from '../utils';
import { ICONS } from '../icons';
import { ListFieldValue } from 'app/shared/components/form-builder/list/list.component';

interface EditorValues {
    label?: string;
    name?: string;
    entities?: string[];
    color?: string;
    values?: ListFieldValue<LabelValuePayload>[];
}

export class LabelDetailsSectionHandler implements FormSectionHandler<LabelResponse, EditorValues> {

    type = 'form';
    icon = ICONS.label;
    bulk = false;

    title = this.translate('TITLE');
    description = this.translate('DESC');

    private colors = ['blue', 'lightblue', 'green', 'olive', 'orange', 'pink', 'purple', 'red', 'teal', 'yellow', 'white', 'gray'];
    private colorField = (entity: { color?: string}) => {
        const items = this.colors.map(x => ({ key: x, value: this.admin.translate(`EDITORS.ASSET.DETAILS.COLORS.${x.toUpperCase()}`) }));
        if (entity?.color && !this.colors.includes(entity.color)) {
            items.push({ key: entity.color, value: entity.color });
        }
        return ComboField('color', this.translate('FIELDS.COLOR'), false, items.sort((a, b) => a.value.localeCompare(b.value)));
    }

    constructor(
        public admin: AdminEditorService,
        public ownerId: string,
        private creating: boolean,
        private entities: FormBuilderKeyValue[],
    ) {
    }

    translate(key: string, values?: Object): string {
        return this.admin.translate(`EDITORS.LABEL.DETAILS.${key}`, values);
    }

    applyUpdate(entity: LabelResponse, payload: LabelUpdateRequest): Promise<LabelResponse> {
        return this.admin.applyUpdate(api => api.entities.updateLabel(entity.id, payload));
    }

    async mapEntityToEditor(entity: LabelResponse): Promise<EditorValues> {
        const result = {
            name: entity.name,
            label: entity.label,
            entities: entity.entities,
            color: entity.color || null,
            values: (entity.values || []).sort((a, b) => a.name.localeCompare(b.name)).map(this.valueToListItem)
        };
        Object.keys(result).forEach(key => result[key] === undefined && delete result[key]); // strip undefined properties from the object
        return Promise.resolve(result);
    }
    
    async mapEditorToEntity(entity: LabelUpdateRequest, values: EditorValues): Promise<LabelUpdateRequest> {
        
        
        const patch: LabelUpdateRequest = {
            name: values.name,
            label: values.label,
            entities: values.entities,
            color: 'color' in values ? values.color || null : undefined,
            values: values.values && values.values.map(x => x.data),
        };
        
        Object.keys(patch).forEach(key => patch[key] === undefined && delete patch[key]); // strip undefined properties from the object
        return EntityUtils.mergeFields(['name', 'label', 'entities', 'color', 'values'], patch, entity, false);

    }

    valueToListItem(value: LabelValuePayload): ListFieldValue<LabelValuePayload> {
        return {
            id: value.label,
            name: value.name,
            icon: ICONS.label,
            color: value.color,
            data: value,
        };
    }

    sanitizeLabel(name: string): string {
        return name.toLowerCase().replace(/[^a-z0-9]/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '');
    }

    async getFields(entity: LabelResponse): Promise<FormBuilderField[]> {
        return [
            {
                ...TextField('name', this.translate('FIELDS.NAME'), true, 2, 30), onChange: async (_field, values) => {
                    if (this.creating) {
                        values.label = this.sanitizeLabel(values.name);
                    }
                },
             },
            { ...TextField('label', this.translate('FIELDS.LABEL'), true, 2, 20), readonly: !this.creating, description: this.creating && this.translate('FIELDS.LABEL_DESC') },
            this.colorField(entity),
            { ...CheckListField('entities', this.translate('FIELDS.ENTITIES'), true, this.entities), description: this.translate('FIELDS.ENTITIES_DESC'), getText: (field, values) => (values[field.id] || []).map(x => this.entities.find(d => d.key === x)?.value || x).join(', ') },
            !this.creating && {
                id: 'values', title: this.translate('FIELDS.VALUES'), type: 'list', required: false,
                actions: [
                    { name: this.admin.i18n.instant('SHARED.ADD'), click: this.showEditValueModal.bind(this) },
                    { id: 'edit', click: this.showEditValueModal.bind(this), },
                ],
                getText: (field: FormBuilderField, values: any) => values[field.id] && values[field.id].map(x => x.name).join(', '),
            },
        ].filter(x => x);
    }

    showEditValueModal(_action: string, _field: FormBuilderField, values: EditorValues, item?: ListFieldValue<LabelValuePayload>): Promise<LabelValuePayload> {

        return this.admin.showFormModal({
            groups: [{
                name: this.translate('MODAL.TITLE'),
                description: this.translate('MODAL.DESC'),
                fields: [
                    {
                        ...TextField('name', this.translate('FIELDS.NAME'), true, 2, 30), onChange: async (_field, values) => {
                            if (!item) {
                                values.label = this.sanitizeLabel(values.name);
                            }
                        }, },
                    { ...TextField('label', this.translate('FIELDS.LABEL'), true, 2, 20), readonly: !!item, description: this.translate('FIELDS.LABEL_DESC') },
                    this.colorField(item?.data),
                ],
            }],
        }, item?.data || {} as LabelValuePayload
        ).then(result => {
            if (result) {
                const existing = item && values.values.find(x => x.id === item?.data.label);
                if (!existing) {
                    values.values.push(this.valueToListItem(result));
                } else {
                    item.name = result.name;
                    item.color = result.color;
                    item.data.name = result.name;
                    item.data.color = result.color;
                }
                return result;
            }
            return null;
        });


    }


}
